import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './Alert.less';

export type TAlertType = 'primary' | 'success' | 'warning' | 'danger' | 'panel';

/**
 * Provides feedback messages for user actions.
 */
export const Alert: React.FC<{
  bubble?: boolean,
  children?: React.ReactNode,
  className?: string,
  hasIcon?: boolean,
  message?: string,
  onClose?: () => void,
  toast?: boolean,
  timeout?: number,
  type?: TAlertType,
  showCloseButton?: boolean,
}> = ({
  bubble, children, className = '', hasIcon, message = '', onClose, toast, type = 'primary', timeout, showCloseButton = true,
}) => {
  const content = children || message;

  // Automatically close the Alert after timeout ms have passed.
  useEffect(() => {
    if (message === '' || timeout === 0 || !onClose) { return () => false; }

    const t = setTimeout(() => { onClose(); }, timeout);
    return () => { clearTimeout(t); };
  }, [message, timeout, onClose]);

  return (
    <div
      role="alert"
      className={`Alert
        ${type}
        ${bubble ? 'bubble toast_message' : ''}
        ${toast ? 'toast' : ''}
        ${content ? 'bubble_show toast_show' : 'bubble_hide toast_hide'}
        ${onClose ? 'dismissible' : 'not_dismissable'}
        ${hasIcon ? 'has_icon' : ''}
        ${className}
      `}
    >
      {hasIcon ? <p className="alert_content">{content}</p> : <div className="alert_content">{content}</div>}
      {!!onClose && content && showCloseButton && (
        <button type="button" className="close_message" onClick={onClose}>
          Close
        </button>
      )}
    </div>
  );
};


/**
 * Convenience function used to make a dismissable bubble alert appear.
 * Similar to web/common/frontend/js/alertBubble.js.
 */
export const useAlertBubble = (timeout = 2500, initialAlertMessage = ''): {
  Alert: React.ReactNode,
  setAlert: React.Dispatch<React.SetStateAction<string>>,
} => {
  const [bubbleMessage, setBubbleMessage] = useState(initialAlertMessage);
  const onCloseAlert = useCallback(() => { setBubbleMessage(''); }, []);

  return {
    Alert: <Alert bubble message={bubbleMessage} onClose={onCloseAlert} timeout={timeout} />,
    setAlert: setBubbleMessage,
  };
};


Alert.propTypes = {
  /** Displays the component as a sliding toast bubble. */
  bubble: PropTypes.bool,
  /** Children elements or components. This allows the alert content to be customizable. */
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  /** Additional CSS classes of the component. */
  className: PropTypes.string,
  /** Displays an icon before the message. */
  hasIcon: PropTypes.bool,
  /** Message text displayed in the component if no children are provided. */
  message: PropTypes.string,
  /** Event handler that runs when the component is closed by the user. */
  onClose: PropTypes.func,
  /** Displays the component as a sliding toast message. */
  toast: PropTypes.bool,
  /** How long the Alert should stay open (in ms) before calling onClose. Use 0 to keep the Alert open indefinitely. */
  timeout: PropTypes.number,
  /** Provides contextual styling. */
  type: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'panel']),
  /** If false, the Alert won't show a close "X" button. Defaults to true. */
  showCloseButton: PropTypes.bool,
};

Alert.defaultProps = {
  bubble: false,
  children: null,
  className: '',
  hasIcon: false,
  message: '',
  onClose: undefined,
  toast: false,
  type: 'primary',
  timeout: 0,
  showCloseButton: true,
};

export default Alert;
